import React, { useState, useEffect, useContext } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Switch,
  TextField,
  Typography,
  Autocomplete,
  Chip,
  IconButton,
} from "@mui/material";
import bkstApi from "../../api";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import PricingVariant from "../component/PricingVariant";
import PricingVariantV2 from "../component/PricingVariantV2";
import Images from "../../request/components/Images";
import ProductRules from "../component/ProductRules";
import ProductSpecialHours from "../component/ProductSpecialHours";
import { EditOutlined, ExpandMoreOutlined, Star, Tune } from "@mui/icons-material";
import QuillRTE from "../../common/component/QuillRTE";
import moment from "moment";
import {
  capitalize,
  getCategoryOptions,
  getVariantOptionSurchargeForDisplay,
  isSalesTaxEnabled,
  isSalesTaxDefaultEnabled,
  getSalesTaxEnabledFromProduct,
} from "../../common/util";
import AddIcon from "@mui/icons-material/Add";
import VariantFieldEditorDialog from "../component/VariantFieldEditorDialog";
import { UserContext } from "../../context/UserContext";
import { FIELDS } from "../../common/constants";
import {
  hasVariantOptionSizeOverrides,
  getVariantOptionSizeOverrideCount,
} from "../../common/variantSizeOverride";

const DEFAULT_CHANNEL = { pos: { status: "ACTIVE" }, online: { status: "ACTIVE" } };

/** Field keys that do not show ordering arrows (photo, inscription, special request). */
const FIELDS_NO_ORDER_ARROWS = ["photo", "inscription", "notes"];

/** Minimum rank for any orderable field (photo, notes, inscription use lower ranks). New fields are added here. */
const MIN_ORDERABLE_RANK = 5;

/** Default rank for size; all other fields have rank lower than size. Kept high so many orderable fields get distinct ranks (no ties). */
const DEFAULT_SIZE_RANK = 100;

/** Keep option.override.size keys in sync when a size is renamed or deleted. */
function syncSizeOverrideKeys(variants, { fromName, toName, deletedSizeName } = {}) {
  if (!variants || typeof variants !== "object") return variants;

  const rename = fromName && toName && fromName !== toName ? { from: fromName, to: toName } : null;
  const deleted =
    deletedSizeName != null && String(deletedSizeName) !== "" ? String(deletedSizeName) : null;
  if (!rename && !deleted) return variants;

  const v = { ...variants };
  for (const key of Object.keys(v)) {
    if (key === "size") continue;
    const variant = v[key];
    if (!variant?.field?.options) continue;
    const options = variant.field.options.map((opt) => {
      const sz = opt.override?.size;
      if (!sz) return opt;

      let nextSize = sz;
      if (rename && Object.prototype.hasOwnProperty.call(nextSize, rename.from)) {
        nextSize = { ...nextSize };
        const entry = nextSize[rename.from];
        delete nextSize[rename.from];
        if (!Object.prototype.hasOwnProperty.call(nextSize, rename.to)) {
          nextSize[rename.to] = entry;
        }
      }
      if (deleted && Object.prototype.hasOwnProperty.call(nextSize, deleted)) {
        if (nextSize === sz) nextSize = { ...nextSize };
        delete nextSize[deleted];
      }
      if (nextSize === sz) return opt;

      const next = { ...opt, override: { ...opt.override, size: nextSize } };
      if (Object.keys(next.override.size).length === 0) {
        delete next.override.size;
        if (Object.keys(next.override).length === 0) delete next.override;
      }
      return next;
    });
    v[key] = { ...variant, field: { ...variant.field, options } };
  }
  return v;
}

export default function ProductEntry(props) {
  const { siteId, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);

  const [product, setProduct] = useState("");

  const [category, setCategory] = useState([]);
  const [images, setImages] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [variants, setVariants] = useState("");
  const [rules, setRules] = useState("");
  const [inventory, setInventory] = useState("");
  const [channel, setChannel] = useState(DEFAULT_CHANNEL);
  const [status, setStatus] = useState("ACTIVE");
  const [collectSalesTax, setCollectSalesTax] = useState(() =>
    isSalesTaxDefaultEnabled(user?.config)
  );

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [variantSettingsOpen, setVariantSettingsOpen] = useState(false);
  const [variantSettingsKey, setVariantSettingsKey] = useState(null);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [showSizeOverrideGrid, setShowSizeOverrideGrid] = useState(false);

  const [productType, setProductType] = useState(null);

  useEffect(() => {
    if (productId) {
      setLoading(true);
      setShowSizeOverrideGrid(false);
      bkstApi(`/my/product/${productId}`).then((res) => {
        setProduct(res.data);

        setCategory(res.data.category || [res.data.type]);

        setImages(res.data.images);
        setTitle(res.data.title);
        setDescription(res.data.description);

        setVariants(res.data.variants);
        setShowSizeOverrideGrid(hasVariantOptionSizeOverrides(res.data.variants));

        setRules(res.data.rules);
        setChannel(res.data.channel || DEFAULT_CHANNEL);
        setInventory(res.data.inventory || "");

        setStatus(res.data.status);
        setCollectSalesTax(getSalesTaxEnabledFromProduct(res.data.salesTax, user?.config));
        setProductType(res.data.type || null);

        setLoading(false);
      });
    } else {
      setShowSizeOverrideGrid(false);
      if (location.state) {
        const p = location.state.product;
        setProduct(p);

        setCategory(p.category || [p.type]);
        setImages(p.images);
        setTitle(p.title);
        setDescription(p.description);

        setVariants(p.variants);
        setShowSizeOverrideGrid(hasVariantOptionSizeOverrides(p.variants));
        setChannel(p.channel || DEFAULT_CHANNEL);

        setRules(p.rules);
        setStatus(p.status);
        setCollectSalesTax(getSalesTaxEnabledFromProduct(p.salesTax, user?.config));
        setProductType(p.type || null);

        if (p.inventory) {
          setInventory(p.inventory);
        }
      }
      setLoading(false);
    }
  }, [productId]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // validate
    let arr = [];
    if (status === "ACTIVE") {
      if (variants) {
        for (const [k, v] of Object.entries(variants)) {
          if (v?.field?.type === "select" && v?.field?.options?.length === 0) {
            arr.push(`${capitalize(v.name)} has no options`);
          }
        }
        if (variants.size) {
          if (variants.size?.field?.options?.length === 0) {
            arr.push(`Pricing required`);
          }
        } else {
          arr.push(`Pricing required`);
        }
      } else {
        arr.push(`Pricing required`);
      }

      if (rules?.date?.max) {
        if (moment().isAfter(moment(rules?.date?.max), "day")) {
          arr.push("Check Advanced Options. Max date can't be in the past");
        }

        if (rules?.date?.min && moment(rules.date.min).isAfter(moment(rules?.date?.max), "day")) {
          arr.push("Check Advanced Options. Min date can't be after Max date");
        }
      }

      if (channel && typeof channel === "object") {
        const hasActiveChannel = Object.values(channel).some((ch) => ch.status === "ACTIVE");
        if (!hasActiveChannel) {
          arr.push("At least one channel must be ACTIVE");
        }
      }
    }

    setErrors(arr);
    if (arr.length > 0) {
      return;
    }

    let payload = {
      category,
      images,
      title,
      description,
      variants,
      rules,
      channel,
      status,
      salesTax: { enabled: collectSalesTax },
    };

    if (inventory) {
      payload.inventory = inventory;
    }

    if (productType) {
      payload.type = productType;
    }

    if (productId) {
      bkstApi.put(`/my/product/${productId}`, payload).then((res) => {
        navigate("/catalog/standard");
      });
    } else {
      bkstApi.post(`/my/product`, payload).then((res) => {
        navigate("/catalog/standard");
      });
    }
    localStorage.removeItem("products");
  };

  const sortVariants = ([k1, v1], [k2, v2]) => {
    return v2.rank - v1.rank || k2.localeCompare(k1);
  };

  /** Variant keys that participate in ordering (excludes size, photo, inscription, notes). */
  const getOrderableEntries = () =>
    Object.entries(variants || {})
      .filter(([k]) => k !== "size" && !FIELDS_NO_ORDER_ARROWS.includes(k))
      .sort(sortVariants);

  const sortBySurcharge = (o1, o2) => {
    const amt1 = o1.pricing?.amount || o1.pricing?.factor || 0;
    const amt2 = o2.pricing?.amount || o2.pricing?.factor || 0;
    let rslt = +amt1 - +amt2;
    if (rslt === 0) {
      rslt = o1.name.localeCompare(o2.name);
    }

    return rslt;
  };

  /** Updates size options and keeps option.override.size keys in sync on rename/delete. */
  const handleSizeOptionsChange = (data, meta) => {
    setVariants((prev) => {
      let v = { ...(prev || {}) };
      let size = v?.["size"] || {
        name: "Size",
        description: "",
        rank: DEFAULT_SIZE_RANK,
        required: true,
        field: { type: "select", options: [] },
      };
      size = { ...size, field: { ...size.field, options: data } };
      v["size"] = size;
      return syncSizeOverrideKeys(v, {
        fromName: meta?.remapSizeOverrideFrom,
        toName: meta?.remapSizeOverrideTo,
        deletedSizeName: meta?.deletedSizeName,
      });
    });
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    const categories = getCategoryOptions();
    const orderableEntries = getOrderableEntries();
    const variantFieldRows = Object.entries(variants || {})
      .sort(sortVariants)
      .map(([key, value]) => {
        if (key === "size") {
          return <React.Fragment key={key} />;
        }
        const showOptionChips =
          value?.field?.type === "select" && !FIELDS_NO_ORDER_ARROWS.includes(key);
        const fieldTypeLabel =
          value?.field?.type === "select"
            ? "select field"
            : value?.field?.type === "text"
              ? "text field"
              : value?.field?.type === "photo"
                ? "photo field"
                : "field";
        const optionCount = Array.isArray(value?.field?.options) ? value.field.options.length : 0;
        const fieldName = (value?.name ?? key).toUpperCase();
        const metadataParts = [
          fieldTypeLabel,
          ...(value?.field?.type === "select"
            ? [`${optionCount} ${optionCount === 1 ? "option" : "options"}`]
            : []),
          value?.required ? "required" : "optional",
        ];
        const fieldMetadata = metadataParts.join(" • ");
        return (
          <Grid item xs={12} key={key}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Accordion
                elevation={0}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreOutlined />}
                  sx={{
                    minHeight: 56,
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography color="text.primary" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      {fieldName}
                    </Typography>
                    <Typography variant="overline" color="text.secondary">
                      {fieldMetadata}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {value?.description?.trim() && (
                    <Box>
                      <Typography
                        variant="body2"
                        color={value?.description?.trim() ? "text.secondary" : "text.disabled"}
                        sx={!value?.description?.trim() ? { fontStyle: "italic" } : undefined}
                      >
                        {value?.description?.trim() || ""}
                      </Typography>
                    </Box>
                  )}
                  {showOptionChips && (
                    <Box mt={2}>
                      {value?.field?.options?.length === 0 && (
                        <Alert severity="info">
                          Add options in the field editor (click the edit icon in the header)
                        </Alert>
                      )}
                      {(value?.field?.options ?? []).map((d, idx) => {
                        const surcharge = getVariantOptionSurchargeForDisplay(d);
                        const overrideCount = getVariantOptionSizeOverrideCount(d);
                        const hasOverrides = overrideCount > 0;
                        const overrideLabel = hasOverrides
                          ? `${overrideCount} ${overrideCount === 1 ? "override" : "overrides"}`
                          : "";
                        const details = [surcharge, overrideLabel].filter(Boolean).join(" | ");
                        const chipIcon =
                          surcharge || hasOverrides ? (
                            <Box
                              component="span"
                              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
                            >
                              {surcharge ? <Star fontSize="small" /> : null}
                              {hasOverrides ? <Tune fontSize="small" /> : null}
                            </Box>
                          ) : undefined;
                        return (
                          <Chip
                            key={`${key}-${idx}`}
                            icon={chipIcon}
                            label={`${d.name}${details ? ` | ${details}` : ""}`}
                            sx={{
                              mr: 0.5,
                              mb: 0.5,
                              "& .MuiChip-icon": {
                                color: "text.primary",
                                ...(surcharge &&
                                  hasOverrides && {
                                    width: "auto",
                                    ml: 1,
                                    mr: 0.25,
                                  }),
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  pr: 0.5,
                  // Match AccordionSummary row height so edit icon aligns with chevron
                  minHeight: 56,
                }}
              >
                <IconButton
                  size="small"
                  aria-label={`Edit ${value?.name}`}
                  onClick={() => {
                    setVariantSettingsKey(key);
                    setVariantSettingsOpen(true);
                  }}
                  sx={{ padding: 0.5 }}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        );
      });

    // Check if editing should be disabled (selection rules only; size overrides are editable via grid)
    const hasSelection = product?.variants?.size?.field?.options?.some((opt) => opt.selection);
    const isEditDisabled = hasSelection;

    return (
      <Container maxWidth="md">
        {isEditDisabled && (
          <Alert severity="error" sx={{ mt: 3, mb: 3, py: 2 }}>
            <Typography>
              This product has complex rules that are not editable in this app. Please contact
              BakeStreet using LiveChat or email for any changes.
            </Typography>
          </Alert>
        )}
        <Box>
          <Box mt={6} mb={3}>
            <Typography variant="h3" gutterBottom>
              Item Details
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Add title, description, photos. Add multiple size / price variants. Create custom
              fields like flavor, add surcharge to premium options. Control availability by channel.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <fieldset disabled={isEditDisabled} style={{ border: "none", padding: 0, margin: 0 }}>
              <Paper elevation={0}>
                <Box px={2} pt={4} pb={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Images
                            edit={true}
                            showArrows={true}
                            pics={images}
                            onChange={(arr) => setImages(arr)}
                            folder={`img/site/${siteId}/tmp`}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <Box>
                            <TextField
                              id="title"
                              value={title}
                              label="Title"
                              name="title"
                              variant="outlined"
                              fullWidth
                              required
                              onChange={(e) => setTitle(e.target.value)}
                              inputProps={{ maxLength: 40 }}
                            />
                          </Box>
                          <Box mt={3}>
                            <Autocomplete
                              value={category}
                              getOptionDisabled={() => category.length >= 5}
                              onChange={(e, val) => {
                                setCategory(val.splice(0, 5));
                              }}
                              options={categories}
                              filterSelectedOptions
                              renderInput={(params) => (
                                <TextField
                                  variant="outlined"
                                  {...params}
                                  label="Category"
                                  required={category.length === 0}
                                  helperText="Choose item type (e.g. Cakes), occasion (e.g. Thanksgiving) or dietary (e.g. Vegan) to show item in selected categories."
                                />
                              )}
                              multiple
                              freeSolo
                              clearOnBlur={true}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <QuillRTE value={description} onChange={(html) => setDescription(html)} />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
              <Box my={4}>
                {user.config?.fees?.model?.includes("cost+") ? (
                  <PricingVariantV2
                    value={(variants?.size?.field?.options || []).sort(sortBySurcharge) || []}
                    onChange={handleSizeOptionsChange}
                  />
                ) : (
                  <PricingVariant
                    value={(variants?.size?.field?.options || []).sort(sortBySurcharge) || []}
                    onChange={handleSizeOptionsChange}
                  />
                )}
              </Box>
              <Box my={4}>
                <Box>
                  <Grid container alignItems="center">
                    <Grid item xs={9}>
                      <Typography variant="h6">Fields</Typography>
                    </Grid>
                    <Grid item xs={3} align="right">
                      <IconButton
                        onClick={() => setAddFieldDialogOpen(true)}
                        aria-label="Add field"
                      >
                        <AddIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
                <Paper elevation={0}>
                  <Box px={2} pt={3} pb={2}>
                    {Object.keys(variants).length === 0 && (
                      <Box px={2} py={4} textAlign="center">
                        <Typography variant="subtitle1">Add Options</Typography>
                        <Typography variant="body1" color="textSecondary">
                          Click + to add options like flavor, filling, etc
                        </Typography>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      {variantFieldRows}
                    </Grid>
                  </Box>
                </Paper>
              </Box>
              <Box my={4}>
                <Box>
                  <Grid container alignItems="center">
                    <Grid item xs={9}>
                      <Typography variant="h6">Settings</Typography>
                    </Grid>
                    <Grid item xs={3} align="right"></Grid>
                  </Grid>
                </Box>
                <Paper elevation={0}>
                  <Box px={2} pt={3} pb={2}>
                    <Box>
                      <Box>
                        <Typography variant="h6">Availability</Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={status === "ACTIVE"}
                            onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "DRAFT")}
                          />
                        }
                        label="Item is available!"
                      />
                    </Box>
                    {status === "ACTIVE" && (
                      <Box py={3}>
                        <Divider />
                        <Box py={2}>
                          <Typography variant="h6">Channel</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Set Item availability by channel
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              sx={{ gap: "10px" }}
                              control={
                                <Switch
                                  size="small"
                                  checked={channel.pos.status === "ACTIVE"}
                                  onChange={(e) =>
                                    setChannel({
                                      ...channel,
                                      pos: { status: e.target.checked ? "ACTIVE" : "DRAFT" },
                                    })
                                  }
                                />
                              }
                              label="POS"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              sx={{ gap: "10px" }}
                              control={
                                <Switch
                                  size="small"
                                  checked={channel.online.status === "ACTIVE"}
                                  onChange={(e) =>
                                    setChannel({
                                      ...channel,
                                      online: { status: e.target.checked ? "ACTIVE" : "DRAFT" },
                                    })
                                  }
                                />
                              }
                              label="Online Store"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    {isSalesTaxEnabled(user.config) && (
                      <Box py={3}>
                        <Divider />
                        <Box py={2}>
                          <Typography variant="h6">Sales Tax</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Control whether sales tax applies to this item
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={collectSalesTax}
                              onChange={(e) => setCollectSalesTax(e.target.checked)}
                            />
                          }
                          label="Collect sales tax"
                        />
                      </Box>
                    )}
                    {status === "ACTIVE" && (
                      <Box>
                        <Divider />
                        <Accordion elevation={0} disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                            <Box>
                              <Typography variant="h6">Availability Rules</Typography>
                              <Typography variant="body2" color="textSecondary">
                                Limit item availability by lead time, date range & days of week
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <ProductRules value={rules} onChange={(data) => setRules(data)} />
                          </AccordionDetails>
                        </Accordion>
                        <Divider />
                        <Accordion elevation={0} disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                            <Box>
                              <Typography variant="h6">Blackout Dates</Typography>
                              <Typography variant="body2" color="textSecondary">
                                Make this item unavailable on specific dates. Exceptions to your
                                availability rules.
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <ProductSpecialHours
                              value={rules?.date?.specialHours || {}}
                              onChange={(specialHours) =>
                                setRules({
                                  ...rules,
                                  date: { ...rules?.date, specialHours },
                                })
                              }
                            />
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
              <Box my={4}>
                {errors?.length > 0 && (
                  <Box my={2}>
                    <Alert severity="error">{errors.join(". ")}</Alert>
                  </Box>
                )}
                <Box my={2} sx={{ display: "flex", gap: "15px" }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(`/shop`)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={
                      isEditDisabled ||
                      !variants?.size ||
                      !(variants?.size?.field?.options || []).length
                    }
                  >
                    Save Item
                  </Button>
                </Box>
              </Box>
            </fieldset>
          </form>
          <VariantFieldEditorDialog
            open={variantSettingsOpen || addFieldDialogOpen}
            onClose={() => {
              setVariantSettingsOpen(false);
              setVariantSettingsKey(null);
              setAddFieldDialogOpen(false);
            }}
            variantKey={addFieldDialogOpen ? null : variantSettingsKey}
            variants={variants}
            orderableEntries={orderableEntries}
            fieldsNoOrderArrows={FIELDS_NO_ORDER_ARROWS}
            disabled={Object.keys(variants || {})}
            showSizeOverrideGrid={showSizeOverrideGrid}
            onSave={({
              variantKey: key,
              name: newName,
              description: newDescription,
              min: newMin,
              max: newMax,
              required: newRequired,
              newOrderableKeys,
              options: newOptions,
              pricing: newPricing,
            }) => {
              if (key == null) return;
              const o =
                variants && typeof variants === "object" && !Array.isArray(variants)
                  ? { ...variants }
                  : {};
              const variant = o[key];
              const isNew = !variant;
              if (variant) {
                if (newName) variant.name = newName;
                variant.description = newDescription ?? "";
                variant.required = newRequired;
                if (!variant.field) variant.field = {};
                if (newMin !== undefined) variant.field.min = newMin;
                variant.field.max = newMax;
                if (newOptions !== undefined) variant.field.options = newOptions;
                if (newPricing !== undefined) {
                  if (newPricing === null) {
                    delete variant.pricing;
                  } else {
                    variant.pricing = newPricing;
                  }
                }
              } else {
                const fieldType = FIELDS[key]?.field?.type ?? "select";
                o[key] = {
                  name: newName ?? "",
                  description: newDescription ?? "",
                  required: newRequired,
                  ...(FIELDS[key]?.rank != null && { rank: FIELDS[key].rank }),
                  field: {
                    type: fieldType,
                    min: fieldType === "select" || fieldType === "photo" ? (newMin ?? 1) : 0,
                    max: newMax ?? 1,
                    options: newOptions ?? [],
                  },
                };
                if (newPricing != null) {
                  o[key].pricing = newPricing;
                }
              }
              if (newOrderableKeys && newOrderableKeys.length > 0) {
                const sr = o?.size?.rank ?? DEFAULT_SIZE_RANK;
                const slots = sr - MIN_ORDERABLE_RANK;
                const needSlots = newOrderableKeys.length;
                const effectiveSizeRank =
                  needSlots <= slots ? sr : Math.max(sr, MIN_ORDERABLE_RANK + needSlots);
                if (effectiveSizeRank > sr && o.size) {
                  o.size = { ...o.size, rank: effectiveSizeRank };
                }
                newOrderableKeys.forEach((k, i) => {
                  if (o[k]) {
                    o[k] = {
                      ...o[k],
                      rank: Math.max(MIN_ORDERABLE_RANK, effectiveSizeRank - 1 - i),
                    };
                  }
                });
              } else if (isNew && key !== "size" && !FIELDS_NO_ORDER_ARROWS.includes(key)) {
                const orderableCount = Object.keys(o).filter(
                  (k) => k !== "size" && !FIELDS_NO_ORDER_ARROWS.includes(k)
                ).length;
                const sr = o?.size?.rank ?? DEFAULT_SIZE_RANK;
                o[key] = {
                  ...o[key],
                  rank: Math.max(MIN_ORDERABLE_RANK, sr - orderableCount),
                };
              }
              setVariants(o);
            }}
            onDelete={(key) => {
              const o = { ...variants };
              delete o[key];
              setVariants(o);
            }}
          />
        </Box>
      </Container>
    );
  }
}
